import { useEffect, useState } from 'react';

/**
 * Theme preference: explicit light/dark, or follow the OS setting.
 * Persisted under `pacebi-theme`; index.html applies it pre-paint with the
 * same resolution rules to avoid a flash, so keep both in sync.
 */
export type Theme = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'pacebi-theme';
const listeners = new Set<() => void>();

export function getTheme(): Theme {
  try {
    const s = localStorage.getItem(STORAGE_KEY);
    if (s === 'light' || s === 'dark' || s === 'system') return s;
  } catch { /* storage blocked */ }
  return 'system';
}

function systemPrefersDark(): boolean {
  return typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function resolveDark(theme: Theme): boolean {
  return theme === 'dark' || (theme === 'system' && systemPrefersDark());
}

export function applyTheme(theme: Theme): void {
  document.documentElement.classList.toggle('dark', resolveDark(theme));
}

export function setTheme(theme: Theme): void {
  try { localStorage.setItem(STORAGE_KEY, theme); } catch { /* storage blocked */ }
  applyTheme(theme);
  listeners.forEach((fn) => fn());
}

/** Toggle order: light → dark → system → light. */
export function nextTheme(theme: Theme): Theme {
  return theme === 'light' ? 'dark' : theme === 'dark' ? 'system' : 'light';
}

/**
 * Current preference + setter, shared across all mounted consumers. Also
 * re-applies the resolved class when the OS scheme changes while in
 * system mode.
 */
export function useTheme(): [Theme, (t: Theme) => void] {
  const [theme, setLocal] = useState<Theme>(getTheme);

  useEffect(() => {
    const sync = () => setLocal(getTheme());
    listeners.add(sync);
    return () => { listeners.delete(sync); };
  }, []);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => { if (getTheme() === 'system') applyTheme('system'); };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return [theme, setTheme];
}
